import { BaseQueryFn } from '@reduxjs/toolkit/query'
import { refreshToken } from 'services/_core/refreshToken'
import { handleError, type AppError, type ErrorResponse } from 'services/_core/handleError'
import type { BaseQueryArgs, QueryArgs } from 'services/_core/types'
import { axiosInstance } from 'services/_core/axiosInstance'
import type { AxiosResponse } from 'axios'

let isRefreshing = false
const refreshQueue: Array<{ resolve: () => void }> = []

const axiosBaseQuery =
  (baseQueryArgs: BaseQueryArgs): BaseQueryFn<QueryArgs, unknown, unknown> =>
  async (args) => {
    try {
      return await axiosInstance(args, baseQueryArgs)
    } catch (error) {
      const err = error as AppError
      return await handleErrorWithRefresh(err, args, baseQueryArgs)
    }
  }

export default axiosBaseQuery

type QueuedRequestPromise = Promise<ErrorResponse | AxiosResponse>

const getQueuedRequestPromise = (
  args: QueryArgs,
  baseQueryArgs: BaseQueryArgs,
): QueuedRequestPromise =>
  new Promise<void>((resolve) => {
    refreshQueue.push({ resolve })
  }).then(async () => {
    try {
      return await axiosInstance(args, baseQueryArgs)
    } catch (error) {
      const err = error as AppError
      return await handleErrorWithRefresh(err, args, baseQueryArgs)
    }
  })

export const retryQueuedRequests = () => {
  refreshQueue.forEach((request) => request.resolve())
}

const handleErrorWithRefresh = async (
  error: AppError,
  args: QueryArgs,
  baseQueryArgs: BaseQueryArgs,
) => {
  if (error.response && error.response.status === 401) {
    // user is unauthorized, refresh token and retry
    removeToken()

    // if already refreshing, return a promise
    // this promise will resolve after the refresh is complete
    if (isRefreshing) {
      return getQueuedRequestPromise(args, baseQueryArgs)
    }

    // start refreshing
    isRefreshing = true

    try {
      const { access_token } = await refreshToken()
      if (!access_token) {
        // refresh failed, logout
        redirectToLogin()
        // won't reach here, just to make typescript happy
        return handleError(error, args, baseQueryArgs)
      }
      localStorage.setItem('token', access_token)
    } catch (error) {
      const err = error as AppError
      // refresh failed, logout
      redirectToLogin()
      return handleError(err, args, baseQueryArgs)
    }

    // success, empty queue
    isRefreshing = false
    retryQueuedRequests()

    // retry the original request
    try {
      return await axiosInstance(args, baseQueryArgs)
    } catch (error) {
      const err = error as AppError
      return handleError(err, args, baseQueryArgs)
    }
  } else {
    return handleError(error, args, baseQueryArgs)
  }
}

const redirectToLogin = () => {
  window.location.href = '/app/login'
}

const removeToken = () => {
  localStorage.removeItem('token')
}
